import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import logo_grey from "../../images/logo_grey.png";
import { useKeycloak } from "@react-keycloak/web";
import { NavLink } from "react-router-dom";
import { HelpIcon, NavBarSortedUp, NavBarSortedDown, TranslateIcon, LightMode, DarkMode } from "./ProjectIcons";
import type { KeycloakTokenParsed } from 'keycloak-js';
import { useTheme } from "../../context/ThemeContext";
import { EulaModal } from "./EulaModal";
import { getEula } from "../../api/users/usersApi";
import SelectInput from "./SelectInput";
import { portalLanguages } from "./ObjectTypes";
// Import multilanguage utilities as needed
import { useTimezone } from "../../context/TimezoneContext";
import { getUtcOffsets } from "./getUtcOffsets";
import { KEYCLOAK_INTENTIONAL_LOGOUT_KEY } from "./useKeycloak";

type ParsedToken = KeycloakTokenParsed & {
  email?: string
  preferred_username?: string
  given_name?: string
  family_name?: string
}
// Track navbar visibility based on scroll

const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { name: 'local', color: 'local-env-stripe' };
  } else if (hostname.includes('dev.gundiservice.org')) {
    return { name: 'dev', color: 'dev-env-stripe' };
  } else if (hostname.includes('stage.gundiservice.org')) {
    return { name: 'stage', color: 'stage-env-stripe' };
  } else {
    return { name: '', color: '' };
  }
};

const Nav = () => {
  const { t, i18n } = useTranslation();
  const { keycloak } = useKeycloak();
  const divRef = useRef<HTMLDivElement | null>(null);
  // Check both localStorage and keycloak.authenticated to handle session expiration
  const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null
  const [timezones, setTimezones] = useState<any[]>([]);
  const { timezone, setTimezone } = useTimezone();
  const [subMenu, setSubMenu] = useState(false);
  const [helpMenu, setHelpMenu] = useState(false);
  const [showEULAModal, setShowEULAModal] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(window.pageYOffset);
  const [environment, setEnvironment] = useState({ name: '', color: '' });
  const parsedToken: ParsedToken | undefined = keycloak?.tokenParsed;
  const toggleSubMenu = () => setSubMenu(!subMenu);
  const toggleHelpMenu = () => setHelpMenu(!helpMenu);
  const { darkMode, toggleDarkMode } = useTheme();

  const getEulaStatus = React.useCallback(async () => {
    if (keycloak.token) {
      const results = await getEula(keycloak.token);
      if (!results.accepted) {
        setShowEULAModal(true);
      }
    }
  }, [keycloak.token])

  const logoutUser = () => {
    // Set flag to indicate intentional logout (prevents showing "Session Expired" modal)
    // Use localStorage so it persists even if sessionStorage is cleared
    localStorage.setItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY, 'true');
    keycloak.logout({ redirectUri: window.location.origin });
    localStorage.removeItem("userAuthorized");
    sessionStorage.clear();
    setTimezone({ label: "UTC", value: "+0000" });
  }

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    keycloak.onAuthSuccess = () => {
      if (!keycloak.isTokenExpired(0)) {
        localStorage.setItem("userAuthorized", "true");
        // Clear the intentional logout flag on successful login
        localStorage.removeItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY);
        keycloak.loadUserProfile();
        getEulaStatus();
      }
    };

    function handleClickOutside(event: any) {
      if (divRef.current && !divRef.current.contains(event.target) && !event.target.closest("a")) {
        setSubMenu(false);
        setHelpMenu(false);
      }
    }

    let temp = getUtcOffsets();
    setTimezones(temp);

    setEnvironment(getEnvironment());

    const updateNavbarVisibility = () => {
      const currentScrollY = window.pageYOffset;

      // Navbar hide/show on scroll is temporarily disabled.
      // When re-enabling, consider using a scroll threshold to prevent flickering on small movements:
      // const SCROLL_THRESHOLD = 10;
      // const scrollDelta = currentScrollY - lastScrollY.current;
      // if (scrollDelta < -SCROLL_THRESHOLD) {
      //   setVisible(true);
      // } else if (scrollDelta > SCROLL_THRESHOLD) {
      //   setVisible(false);
      // }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", updateNavbarVisibility);
    // Attach the event listener when the component mounts.
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener("scroll", updateNavbarVisibility);
    };
  }, [keycloak, getEulaStatus]);

  const handleSelectTimezone = (e: any) => {
    setTimezone(e);
    localStorage.setItem("selectedTimezone", JSON.stringify(e));
  }

  return (
    <div>
      <div className={`fixed top-0 z-50 w-full transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}>
        {environment.name !== 'production' && environment.name !== '' && (
          <div className={`${environment.color} text-xs font-bold text-center py-1.5`}></div>
        )}
        <section className="x-auto">
          <nav className="bg-white dark:bg-off-black text-secondary-gray dark:text-secondary-background w-screen shadow-sm shadow-gray-lines dark:shadow-neutral-gray-900">
            <div className="flex justify-between px-5 xl:px-12 py-6 w-full items-center">
              <div className="flex flex-row items-end">
                <img src={darkMode ? logo_grey : require('../../images/gundi_logo.png')} alt="gundi logo" className="h-8 mr-2" />
                <h3 className="text-2xl font-bold text-secondary-gray dark:text-secondary-background font-heading -mb-1 mr-6 pb-0">
                  {process.env.REACT_APP_TEST_VAR}
                </h3>

                {isLoggedIn && (
                  <ul className="hidden md:flex px-4 mx-auto font-semibold font-heading space-x-12">
                    <li>
                      <NavLink data-testid="nav_connections_btn" to="/">
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green dark:text-accent-green border-2 border-white dark:border-off-black border-b-accent-green dark:border-b-accent-green" : "hover:text-neutral-gray-400 dark:hover:text-neutral-gray-100 border-2 border-white dark:border-off-black"}>{t("Connections")}</span>)}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink data-testid="nav_destinations_btn" to="/destinations">
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green dark:text-accent-green border-2 border-white dark:border-off-black border-b-accent-green dark:border-b-accent-green" : "hover:text-neutral-gray-400 dark:hover:text-neutral-gray-100 border-2 border-white dark:border-off-black"}>{t("Destinations")}</span>)}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink data-testid="nav_sources_btn" to="/sources">
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green dark:text-accent-green border-2 border-white dark:border-off-black border-b-accent-green dark:border-b-accent-green" : "hover:text-neutral-gray-400 dark:hover:text-neutral-gray-100 border-2 border-white dark:border-off-black"}>{t("Sources")}</span>)}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink data-testid="nav_organizations_btn" to="/workspaces">
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green dark:text-accent-green border-2 border-white dark:border-off-black border-b-accent-green dark:border-b-accent-green" : "hover:text-neutral-gray-400 dark:hover:text-neutral-gray-100 border-2 border-white dark:border-off-black"}>{t("Workspaces")}</span>)}
                      </NavLink>
                    </li>
                  </ul>
                )}
              </div>

              <div className="flex flex-row text-neutral-gray-900 dark:text-neutral-gray-100 items-center">
                {isLoggedIn && (
                  <>
                    <div className="hidden md:flex flex-row items-center">
                      {/* tz select */}
                      <div className="mx-3 cursor-pointer flex justify-end w-content">
                        <SelectInput
                          options={timezones}
                          data-testid="select_timezone_activity_logs_filter"
                          className={"basic-single text-sm"}
                          value={timezone} // Use value instead of defaultValue
                          isDisabled={false}
                          isLoading={false}
                          isClearable={false}
                          isSearchable={true}
                          isMulti={false}
                          name={"Filter by Workspace"}
                          onChange={(e: any) => handleSelectTimezone(e)}
                          timezoneSelect={true}
                        />
                      </div>
                      {/* help menu */}
                      <div className="mx-3 cursor-pointer flex justify-end pb-1">
                        <div onClick={toggleHelpMenu}>
                          <HelpIcon helpMenu={helpMenu} />
                        </div>
                        <div className={!helpMenu ? "hidden" : " z-30 absolute bg-light-gray dark:bg-dark-gray mt-7 shadow rounded border border-gray-lines dark:border-neutral-gray-700"}>
                          <ul>
                            <li>
                              <a
                                href="https://support.earthranger.com/en_US/gundi"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer text-off-black dark:text-neutral-gray-100 font-semibold hover:bg-white dark:hover:bg-secondary-gray p-2 rounded block"
                              >
                                {t("Help Center")}
                              </a>
                            </li>
                            <li>
                              <a
                                href="https://support.earthranger.com/en_US/gundi-getting-help/gundi-contact-us"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer text-off-black dark:text-neutral-gray-100 font-semibold hover:bg-white dark:hover:bg-secondary-gray p-2 rounded block"
                              >
                                {t("Contact Support")}
                              </a>
                            </li>
                            <li>
                              <a
                                href="https://projectgundi.org/Legal-Pages/User-Agreement"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer text-off-black dark:text-neutral-gray-100 font-semibold hover:bg-white dark:hover:bg-secondary-gray p-2 rounded block">
                                {t("Gundi EULA")}
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {/* language menu - always visible */}
                <div className="flex justify-end">
                  <SelectInput
                    options={portalLanguages(t)}
                    className={"basic-single mr-3 cursor-pointer"}
                    value={{
                      label: <TranslateIcon />,
                      value: i18n.language
                    }}
                    defaultValue={{
                      label: <TranslateIcon />,
                      value: i18n.language
                    }}
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={true}
                    isMulti={false}
                    name={"Language"}
                    onChange={(e: any) => handleLanguageChange(e.value)}
                    languageSelect={true}
                  />
                </div>
                {/* profile dropdown */}
                {isLoggedIn && (
                  <div className="flex justify-end">
                    <div onClick={toggleSubMenu} className="text-secondary-text dark:text-neutral-gray-100 cursor-pointer">{keycloak?.authenticated ? (parsedToken?.given_name + " " + parsedToken?.family_name) : ""} {subMenu ? <NavBarSortedUp /> : <NavBarSortedDown />}</div>

                    <div ref={divRef} className={!subMenu ? "hidden" : " z-30 absolute bg-light-gray dark:bg-dark-gray mt-7 shadow rounded border border-gray-lines dark:border-neutral-gray-700"}>
                      <ul>
                        <li
                          data-testid="nav_logout_btn"
                          className="cursor-pointer text-off-black dark:text-neutral-gray-100 font-semibold hover:bg-white dark:hover:bg-secondary-gray p-2 rounded"
                          onClick={logoutUser}
                        >
                          {t("Log Out")}
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {/* Dark mode toggle */}
                <div className="mx-3 cursor-pointer" onClick={toggleDarkMode}>
                  {darkMode ? <DarkMode /> : <LightMode />}
                </div>
              </div>
            </div>
          </nav>
        </section>
      </div>
      {showEULAModal &&
        <EulaModal />
      }
    </div>
  );
};

export default Nav;